import React, { useEffect, useState, useRef, useCallback } from 'react'
import { Add } from '@mui/icons-material'
import { Box, Menu, MenuItem } from '@mui/material'
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state'
import { useLazyGetProjectsQuery } from '../../../store/redux/reducers/projectsSlice'
import { Project } from '../../../store/types'
import { useAppSelector } from '../../../store/redux/hooks'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { getIsLeader, getIsManagement } from '../../../store/redux/reducers/currentUserSlice'
import { ExtraSmallSquareButton } from '../../../styles/ButtonStyles'

type AddItemPopupProps = {
  handleItemClick: (project: Project) => void
  isEditionDisabled: boolean
  projectsAdded: Project[]
}

const AddProjectMenuPopup = ({ handleItemClick, isEditionDisabled, projectsAdded }: AddItemPopupProps) => {
  const { t } = useTranslation()
  const [projects, setProjects] = useState<Project[]>([])
  const [allProjects, setAllProjects] = useState<Project[]>([])
  const [last, setLast] = useState(false)
  const [page, setPage] = useState(0)
  const isLeader = useSelector(getIsLeader)
  const isManagement = useSelector(getIsManagement)
  const isManagementAndNotLeader = isManagement && !isLeader
  const { leaderId } = useAppSelector(state => state.leaderId)
  const [getProjects] = useLazyGetProjectsQuery()
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)

  const fetchProjects = useCallback(async () => {
    if (last) return
    const query = isManagementAndNotLeader
      ? { page: page, size: 10 }
      : { page: page, size: 10, 'leaders.leader.id': leaderId }

    const { data, isSuccess } = await getProjects(query)
    if (isSuccess) {
      page === 0
        ? setAllProjects(data.content)
        : setAllProjects(prevAllProjects => [...prevAllProjects, ...data.content])
      setLast(data.last)
    }
  }, [getProjects, isManagementAndNotLeader, leaderId, last, page])

  useEffect(() => {
    setLast(false)
    setPage(0)
  }, [isManagementAndNotLeader])

  useEffect(() => {
    const filteredProjects = allProjects.filter(
      project => !projectsAdded.some(addedProject => addedProject.id === project.id)
    )
    setProjects(filteredProjects)
  }, [allProjects, projectsAdded])

  useEffect(() => {
    fetchProjects()
  }, [fetchProjects])

  const handleScroll = () => {
    if (!scrollContainerRef.current || last) return

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      setPage(prevPage => prevPage + 1)
    }
  }

  return (
    <PopupState variant="popover" popupId="month-menu-popper" disableAutoFocus={false} parentPopupState={null}>
      {popupState => (
        <React.Fragment>
          {!isEditionDisabled ? (
            <ExtraSmallSquareButton
              aria-label={t('page.projectDistribution.aria.addProjectMenu')}
              color="secondary"
              {...bindTrigger(popupState)}
            >
              <Add />
            </ExtraSmallSquareButton>
          ) : (
            <Box sx={{ ml: '20px', borderBottom: '1px solid rgba(224, 224, 224, 1)' }}></Box>
          )}

          <Menu {...bindMenu(popupState)} slotProps={{ paper: { elevation: 5 } }} sx={{ color: 'text.base' }}>
            <Box sx={{ maxHeight: 200, overflowY: 'auto' }} onScroll={handleScroll} ref={scrollContainerRef}>
              {projects.length === 0 ? (
                <MenuItem
                  sx={{
                    color: 'text.base',
                    '&.Mui-disabled': {
                      opacity: 1,
                    },
                  }}
                  disabled={true}
                >
                  {t('page.projectDistribution.noLeaderProjects')}
                </MenuItem>
              ) : (
                projects.map(project => (
                  <MenuItem
                    key={project.id}
                    value={project.name}
                    onClick={() => {
                      handleItemClick(project)
                      setProjects(prevProjects => prevProjects.filter(p => p.id !== project.id))
                    }}
                    sx={{ color: 'text.base', maxWidth: '200px' }}
                  >
                    {project.name}
                  </MenuItem>
                ))
              )}
            </Box>
          </Menu>
        </React.Fragment>
      )}
    </PopupState>
  )
}

export default AddProjectMenuPopup